import type { ReactNode } from 'react';

import { Box, Card, CardContent, Divider, Stack, Typography } from '@mui/material';

export interface SettingsCardProps {
  /** Section title. */
  title: ReactNode;
  /** Optional supporting description below the title. */
  description?: ReactNode;
  /** Optional leading icon. */
  icon?: ReactNode;
  /** Optional right-aligned header action. */
  action?: ReactNode;
  /** Insert dividers between direct children (default: true). */
  divided?: boolean;
  children: ReactNode;
}

/**
 * Titled settings section. Groups related fields in a bordered card with an
 * optional icon/description header and dividers between rows.
 */
export function SettingsCard({
  title,
  description,
  icon,
  action,
  divided = true,
  children,
}: SettingsCardProps) {
  return (
    <Card>
      <CardContent sx={{ p: { xs: 2, md: 3 } }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start" justifyContent="space-between">
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            {icon ? (
              <Box
                sx={{
                  display: 'grid',
                  placeItems: 'center',
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  flexShrink: 0,
                  color: 'primary.main',
                  bgcolor: 'action.hover',
                }}
              >
                {icon}
              </Box>
            ) : null}
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6">{title}</Typography>
              {description ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  {description}
                </Typography>
              ) : null}
            </Box>
          </Stack>
          {action ? <Box sx={{ flexShrink: 0 }}>{action}</Box> : null}
        </Stack>

        <Divider sx={{ mt: 2 }} />

        <Stack divider={divided ? <Divider flexItem /> : undefined}>{children}</Stack>
      </CardContent>
    </Card>
  );
}
